/**
 * Deadline-type catalog — per advisory category.
 *
 * Personalizes the Deadline Radar + Deadline Reminder settings by adviser category.
 * IMMIGRATION IS INTENTIONALLY ABSENT: it keeps its existing hardcoded lists (radar
 * has 16 types, settings has 10) untouched, so IRCC behavior is unchanged. This catalog
 * only drives NON-immigration categories.
 *
 * For every non-immigration category, each entry's `key` is used as BOTH:
 *   - the deadline's `type` stored on the expiration-tracker item, AND
 *   - the `notifyTypes` key toggled in reminder settings.
 * (The backend engine resolves immigration aliases separately; for these categories the
 * key is identity, so no mapping is needed.) `symbol` renders in <option> dropdowns.
 *
 * Labels here are reminder categories, NOT official/legal form fields — safe to edit.
 */

export const GENERIC_DEFAULT_TYPES = [
  { key: 'document_expiry', label: 'Document expiry', symbol: '▦' },
  { key: 'renewal_due', label: 'Renewal due', symbol: '◈' },
  { key: 'payment_due', label: 'Payment due', symbol: '$' },
  { key: 'appointment', label: 'Appointment / Deadline', symbol: '◉' },
  { key: 'contract_expiry', label: 'Contract expiry', symbol: '▣' },
  { key: 'other', label: 'Other', symbol: '•' },
];

export const DEADLINE_CATALOG = {
  legal: [
    { key: 'filing_deadline', label: 'Filing deadline', symbol: '▦' },
    { key: 'hearing_date', label: 'Court / Hearing date', symbol: '⚖' },
    { key: 'limitation_deadline', label: 'Statute of limitations', symbol: '⏳' },
    { key: 'contract_expiry', label: 'Contract expiry', symbol: '▣' },
    { key: 'license_renewal', label: 'License / Bar renewal', symbol: '◈' },
    { key: 'retainer_topup', label: 'Retainer top-up', symbol: '$' },
    { key: 'other', label: 'Other', symbol: '•' },
  ],
  financial: [
    { key: 'tax_filing', label: 'Tax filing', symbol: '▦' },
    { key: 'policy_renewal', label: 'Policy renewal', symbol: '◈' },
    { key: 'contribution_deadline', label: 'Contribution deadline', symbol: '➤' },
    { key: 'portfolio_review', label: 'Portfolio review', symbol: '◉' },
    { key: 'regulatory_filing', label: 'Regulatory filing', symbol: '▣' },
    { key: 'payment_due', label: 'Payment due', symbol: '$' },
    { key: 'other', label: 'Other', symbol: '•' },
  ],
  'health-wellness': [
    { key: 'license_renewal', label: 'License / Cert renewal', symbol: '◈' },
    { key: 'care_plan_expiry', label: 'Care-plan expiry', symbol: '▣' },
    { key: 'follow_up_due', label: 'Follow-up due', symbol: '◉' },
    { key: 'authorization_expiry', label: 'Authorization expiry', symbol: '▦' },
    { key: 'package_expiry', label: 'Package / Membership expiry', symbol: '➤' },
    { key: 'other', label: 'Other', symbol: '•' },
  ],
  business: [
    { key: 'contract_expiry', label: 'Contract / SOW expiry', symbol: '▣' },
    { key: 'license_renewal', label: 'License / Permit renewal', symbol: '◈' },
    { key: 'compliance_filing', label: 'Compliance filing', symbol: '▦' },
    { key: 'invoice_due', label: 'Invoice due', symbol: '$' },
    { key: 'project_milestone', label: 'Project milestone', symbol: '◉' },
    { key: 'insurance_renewal', label: 'Insurance renewal', symbol: '◇' },
    { key: 'other', label: 'Other', symbol: '•' },
  ],
  career: [
    { key: 'cert_renewal', label: 'Certification renewal', symbol: '◈' },
    { key: 'package_expiry', label: 'Package expiry', symbol: '➤' },
    { key: 'goal_deadline', label: 'Goal / Milestone deadline', symbol: '◉' },
    { key: 'session_expiry', label: 'Session / Booking expiry', symbol: '◴' },
    { key: 'application_deadline', label: 'Application deadline', symbol: '▦' },
    { key: 'other', label: 'Other', symbol: '•' },
  ],
  it: [
    { key: 'sla_deadline', label: 'SLA deadline', symbol: '⏳' },
    { key: 'cert_renewal', label: 'Certification renewal', symbol: '◈' },
    { key: 'contract_renewal', label: 'Contract / License renewal', symbol: '▣' },
    { key: 'domain_ssl_expiry', label: 'Domain / SSL expiry', symbol: '◎' },
    { key: 'project_milestone', label: 'Project milestone', symbol: '◉' },
    { key: 'compliance_audit', label: 'Compliance / Audit', symbol: '▦' },
    { key: 'other', label: 'Other', symbol: '•' },
  ],
};

const norm = (v) => String(v || '').trim().toLowerCase();

/** True when the adviser category is immigration (→ keep existing hardcoded lists). */
export function isImmigrationCategory(category) {
  return norm(category).includes('immigration');
}

/**
 * Deadline types for an adviser category (slug or free-text label).
 * Returns `null` for immigration (caller uses its existing immigration lists),
 * the matched catalog list otherwise, or the generic default for unknown categories.
 */
export function getCategoryDeadlineTypes(category) {
  const c = norm(category);
  if (!c || isImmigrationCategory(category)) return null;

  // Direct slug hit first, then tolerant keyword match on the stored label.
  if (DEADLINE_CATALOG[c]) return DEADLINE_CATALOG[c];
  if (c.includes('legal')) return DEADLINE_CATALOG.legal;
  if (c.includes('financ') || c.includes('invest') || c.includes('account')) return DEADLINE_CATALOG.financial;
  if (c.includes('health') || c.includes('wellness') || c.includes('medical') || c.includes('therapy') || c.includes('nutrition') || c.includes('fitness')) return DEADLINE_CATALOG['health-wellness'];
  if (c.includes('business') || c.includes('consult') || c.includes('marketing') || c.includes('startup')) return DEADLINE_CATALOG.business;
  if (c.includes('career') || c.includes('coach') || c.includes('recruit')) return DEADLINE_CATALOG.career;
  // 'it' as a whole word only: a substring match sent Accessibility, Military, Fertility and Benefits to IT.
  if (/it/.test(c) || c.includes('tech') || c.includes('software') || c.includes('cyber') || c.includes('cloud')) return DEADLINE_CATALOG.it;
  return GENERIC_DEFAULT_TYPES;
}

/** Default notifyTypes map (all enabled) for a category's deadline types. */
export function defaultNotifyTypesFor(category) {
  const types = getCategoryDeadlineTypes(category);
  if (!types) return null; // immigration → caller keeps existing immigration defaults
  return types.reduce((m, t) => { m[t.key] = true; return m; }, {});
}
